import type { ReactNode } from 'react';
import type { ViewStyle } from 'react-native';
import { useThemeName, YStack } from 'tamagui';

import { GLASS_OVERLAY_ALPHA } from '@/theme/glass';
import { useShadow } from '@/theme/shadow';
import { borderWidth, darkColors, lightColors } from '@/theme/tokens.generated';

// Glass is for the dock and scrims only (decision 49, issue #627). Every card is
// an opaque SolidSurface; this file keeps the dock's tint plus the shared accent
// color. The overlay tint reuses the app's own surfaceCard, never a generic
// frosted white, so the dock still reads as Nearkin's own warm material.
function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// useThemeName, not a color-scheme media query -- apple-signin-
// button.tsx already established this as the app's pattern for
// reading the resolved theme at runtime (Tamagui's own theme, which
// TamaguiProvider/Theme drive, not the OS setting directly).
export function useGlassTint() {
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const colors = isDark ? darkColors : lightColors;

  return {
    isDark,
    // Alpha is GLASS_OVERLAY_ALPHA: high enough that scrolling content cannot
    // bleed through the dock labels (dock-contrast.test.ts proves 4.5:1).
    overlay: hexToRgba(colors.surfaceCard, isDark ? GLASS_OVERLAY_ALPHA.dark : GLASS_OVERLAY_ALPHA.light),
    border: hexToRgba(colors.borderStrong, isDark ? 0.35 : 0.22),
    // A thin brighter-alpha line along the top edge only -- the classic,
    // cheap "this is glass, not a flat card" cue (light catching the
    // top rim of a real translucent surface) that works regardless of
    // how much blur variation the backdrop actually has. Independent of
    // `border` (which rings the whole shape at even weight) -- this is
    // deliberately ONE edge only, brighter, so it reads as a highlight
    // rather than just a thicker border.
    edgeHighlight: hexToRgba(isDark ? colors.textPrimary : colors.surfacePage, isDark ? 0.16 : 0.08),
  };
}

// Resolved (not a Tamagui token string) brand accent color, for the
// handful of callers that need a real value rather than a `$token` --
// react-native-svg's `stroke`/`fill` props aren't part of Tamagui's
// styling system, so a token string like "$primary" doesn't resolve
// there (confirmed against this app's own existing icon call sites,
// e.g. (app)/_layout.tsx's tab bar, which is handed an already-
// resolved color by React Navigation, never a token). EmptyState's
// icon badges are the first place outside the tab bar/logo marks that
// need this.
export function useGlassAccentColor(): string {
  const themeName = useThemeName();
  return themeName === 'dark' ? darkColors.actionPrimary : lightColors.actionPrimary;
}

// The one card surface: opaque $card fill, hairline border, shadow-1. Glass is
// reserved for the dock and scrims, because blurring a flat ground returns the
// same colour (issue #293) and costs a BlurView per row.
export function SolidSurface({
  children,
  style,
  borderRadius = '$lg',
}: {
  children: ReactNode;
  style?: ViewStyle;
  borderRadius?: number | string;
}) {
  return (
    <YStack
      borderRadius={borderRadius}
      borderWidth={borderWidth.hairline}
      borderColor="$borderColor"
      backgroundColor="$card"
      overflow="hidden"
      {...useShadow(1)}
      style={style}
    >
      {children}
    </YStack>
  );
}
